import * as Api from '@ViewModels';
import { meetingCancelled, meetingCompleted, meetingDraft, meetingScheduled, orange } from '../../../styles/colors';

/** Shared display config for meeting status — used by table + flyout so they stay in sync */
export const MEETING_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
	[Api.MeetingStatus.Scheduled]: { label: 'Scheduled', color: meetingScheduled },
	[Api.MeetingStatus.Completed]: { label: 'Completed', color: meetingCompleted },
	[Api.MeetingStatus.Cancelled]: { label: 'Cancelled', color: meetingCancelled },
	[Api.MeetingStatus.WaitingForDisposition]: { label: 'Scheduled (Past)', color: orange },
	[Api.MeetingStatus.Draft]: { label: 'Draft', color: meetingDraft },
};

/**
 * What a virtual location reads as while its per-booking unique link has not been minted yet. Mirrors the API's own
 * pending text, which it now returns instead of a leftover static link pointing at a different meeting room.
 * Ref: https://github.com/Real-Magic/Levitate/issues/19669
 */
export const PendingUniqueLinkText = 'Use the meeting link in the calendar invite';

/** Get location detail (link, phone number, address, instructions) */
export const getLocationDetail = (location?: Api.IMeetingLocation): string | null => {
	if (!location?._type) return null;
	switch (location._type) {
		case Api.MeetingLocation.Virtual: {
			const virtualLocation = location as Api.IVirtualMeetingLocation;
			// A unique-link location reports no link until the real one exists, so fall back to the pending text rather
			// than dropping the whole location row.
			if (virtualLocation.generateUniqueLink && !virtualLocation.link) {
				return PendingUniqueLinkText;
			}
			return virtualLocation.link ?? null;
		}
		case Api.MeetingLocation.Phone:
			return (location as Api.IPhoneMeetingLocation).phoneNumber ?? null;
		case Api.MeetingLocation.InPerson:
			return (location as Api.IInPersonMeetingLocation).address ?? null;
		case Api.MeetingLocation.Other:
			return (location as Api.IOtherMeetingLocation).instructions ?? null;
		default:
			return null;
	}
};

/** Get location type label */
export const getLocationTypeLabel = (location?: Api.IMeetingLocation): string => {
	if (!location?._type) return '-';
	switch (location._type) {
		case Api.MeetingLocation.Virtual:
			return 'Virtual';
		case Api.MeetingLocation.Phone:
			return 'Phone';
		case Api.MeetingLocation.InPerson:
			return 'In Person';
		case Api.MeetingLocation.Other:
			return 'Other';
		default:
			return '-';
	}
};
